import * as React from 'react'

import { Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'

import DurationInput from 'modules/core/components/DurationInput'
import Modal from 'modules/core/components/Modal'
import Selector from 'modules/core/components/Selector'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import {
  categoryNameWithTimeTypeLabel,
  categoryTimeTypeLabel,
} from 'modules/attendance/components/CustomTimeRanges/Content/utils/categoryNameWithTimeTypeLabel'
import CreateAttendanceCustomTimeRangeCategoryForm from 'modules/attendance/lib/forms/CreateAttendanceCustomTimeRangeCategoryForm'
import { useFetchTimeSettingsCustomTimeRangeCategories } from 'modules/time_categories_settings/hooks/useFetchTimeSettingsCustomTimeRangeCategories'

interface Props {
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  settingId: string
}

const NewCategoryModal = ({ setModalOpen, settingId }: Props) => {
  const [error, setErrors] = React.useState<string>('')
  const {
    company: { id: currentCompanyId },
  } = useCurrentSession()
  const { categories: timeSettingsCategories } = useFetchTimeSettingsCustomTimeRangeCategories(
    currentCompanyId,
    true
  )
  const form = CreateAttendanceCustomTimeRangeCategoryForm({
    settingId,
  })

  const categoriesOptions = timeSettingsCategories.map((category) => {
    const label = categoryNameWithTimeTypeLabel(category.name, category.timeType)
    const timeTypeLabel = categoryTimeTypeLabel(category.timeType)

    return (
      <Selector.Option
        key={category.name}
        value={category.id.toString()}
        label={label}
        searchValue={category.name}
      >
        <Box flexDirection="row" gap="s4" width="full" justifyContent="spaceBetween">
          <Text.Normal ellipsis weight="medium">
            {category.name}
          </Text.Normal>
          <Text.Normal whiteSpace="nowrap">{timeTypeLabel}</Text.Normal>
        </Box>
      </Selector.Option>
    )
  })

  const onSubmit = () => {
    if (
      !form.values.timeSettingsCustomTimeRangeCategoryId ||
      form.values.timeSettingsCustomTimeRangeCategoryId === '0'
    ) {
      setErrors(i18n.t('time_tracking.settings.time_blocks.categories.modals.errors.missing_name'))
      return
    }
    form.handleSubmit()
    setModalOpen(false)
  }

  const onClose = () => {
    form.resetForm()
    setModalOpen(false)
  }

  return (
    <Modal
      title={i18n.t('time_tracking.settings.time_blocks.worked_time_categories.new_value')}
      illustration="monety-bag"
      description={''}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={form.isSubmitting} submit>
            {i18n.t('common.save')}
          </Button>
        </>
      }
    >
      <Box gap="s16">
        <Selector.Popover
          label={i18n.t('time_tracking.settings.time_blocks.categories.modals.category')}
          placeholder={i18n.t('common.select')}
          blankLabel={i18n.t('common.select')}
          value={[form.values.timeSettingsCustomTimeRangeCategoryId]}
          onChange={(value) => {
            form.setFieldValue('timeSettingsCustomTimeRangeCategoryId', value)
          }}
          mandatory
          error={error}
          hasSearch={false}
        >
          {categoriesOptions}
        </Selector.Popover>

        <Box flexDirection="row" alignItems="center" justifyContent="spaceEvenly" gap="s8">
          <Box width="full">
            <DurationInput
              label={i18n.t('time_tracking.settings.time_blocks.categories.modals.raw_hours')}
              minutes={form.values.rawMinutesInCents}
              onChange={(value: number) => form.setFieldValue('rawMinutesInCents', value)}
            />
          </Box>
          <Box paddingTop="s24" alignItems="center" width="fit-content">
            <Icon.Medium icon="arrow-right" />
          </Box>
          <Box width="full">
            <DurationInput
              label={i18n.t(
                'time_tracking.settings.time_blocks.categories.modals.equivalent_hours'
              )}
              minutes={form.values.equivalentMinutesInCents}
              onChange={(value: number) => form.setFieldValue('equivalentMinutesInCents', value)}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default NewCategoryModal
